import { Fragment, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  deleteItemFromCartAsync,
  selectCartLoaded,
  selectCartStatus,
  selectItems,
} from './cartSlice';
import { Link, useNavigate } from 'react-router-dom';
import { Grid } from 'react-loader-spinner';
import Modal from '../common/Modal';
import PaymentMethodModal from '../payment/components/PaymentMethodModal';
import { useLanguage } from '../../context/LanguageContext';
import { useCommonTranslations } from '../../context/commonTranslations';
import {
  ShoppingCartIcon,
  TrashIcon,
  ArrowRightIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';

// Helper function to get the correct price based on plan type
function getPriceByPlanType(product, planType = 'monthly') {
  if (planType === 'yearly') {
    return product.yearlyDiscountPrice || product.yearlyPrice || 0;
  }
  return product.discountPrice || product.price || 0;
}

// Helper function to get multilingual text
function getMultilingualText(field, language = 'en') {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    const result = field[language] || field['en'] || field['fr'] || Object.values(field)[0] || '';
    return typeof result === 'string' ? result : '';
  }
  return '';
}

export default function Cart() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useCommonTranslations(language);

  const items = useSelector(selectItems);
  const status = useSelector(selectCartStatus);
  const cartLoaded = useSelector(selectCartLoaded);
  const [openModal, setOpenModal] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const totalAmount = items.reduce(
    (amount, item) => getPriceByPlanType(item.product, item.planType) * item.quantity + amount,
    0
  );
  const totalItems = items.reduce((total, item) => item.quantity + total, 0);

  const handleRemove = (e, id) => {
    dispatch(deleteItemFromCartAsync(id));
  };

  return (
    <>
      <div className="min-h-screen bg-white py-8 sm:py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-600 text-white">
              <ShoppingCartIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-gray-900">
                {t.cart.pageTitle}
              </h1>
              <p className="text-gray-700 text-sm">
                {totalItems} {totalItems === 1 ? t.cart.itemInCart : t.cart.itemsInCart}
              </p>
            </div>
          </div>

          {/* Loading State */}
          {status === 'loading' && (
            <div className="flex justify-center py-12">
              <Grid
                height="80"
                width="80"
                color="#0ea5e9"
                ariaLabel="grid-loading"
                radius="12.5"
                wrapperStyle={{}}
                wrapperClass=""
                visible={true}
              />
            </div>
          )}

          {/* Empty Cart State */}
          {!items.length && cartLoaded && status !== 'loading' && (
            <div className="bg-white rounded-xl shadow-subtle border border-blue-200 p-12 text-center">
              <ShoppingCartIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t.cart.noItemsTitle}
              </h3>
              <p className="text-gray-700 mb-6">
                {t.cart.noItemsDesc}
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 text-sm"
              >
                <ShoppingBagIcon className="w-4 h-4" />
                {t.cart.browseProducts}
              </Link>
            </div>
          )}

          {/* Cart Items */}
          {items.length > 0 && (
          <div className="bg-white rounded-xl shadow-subtle border border-blue-200 overflow-hidden mb-6">
            <ul className="divide-y divide-blue-200">
              {items.map((item) => (
                <li key={item.id} className="p-6 hover:bg-white transition-colors duration-200">
                  <div className="flex gap-4 sm:gap-6">
                    {/* Product Image */}
                    <div className="h-24 w-24 sm:h-28 sm:w-28 flex-shrink-0 overflow-hidden rounded-xl border border-blue-200 bg-blue-50">
                      <img
                        src={item.product.thumbnail}
                        alt={getMultilingualText(item.product.title, language)}
                        className="h-full w-full object-cover object-center"
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex flex-1 flex-col">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="text-base sm:text-lg font-medium text-gray-900">
                            <Link
                              to={`/product-detail/${item.product.id}`}
                              className="hover:text-blue-700 transition-colors"
                            >
                              {getMultilingualText(item.product.title, language)}
                            </Link>
                          </h3>
                          {item.product.brand && (
                            <p className="mt-1 text-sm text-gray-700">
                              {getMultilingualText(item.product.brand, language)}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end">
                          <p className="text-lg font-medium text-gray-900">
                            €{getPriceByPlanType(item.product, item.planType)}
                          </p>
                          <p className="text-xs text-gray-700 mt-1">
                            {item.planType === 'yearly' ? t.cart.yearly : t.cart.monthly}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-1 items-end justify-between">
                        {/* Plan Type Badge */}
                        <div className="text-xs font-medium text-gray-700 bg-blue-50 px-3 py-1 rounded-lg">
                          {item.planType === 'yearly' ? t.cart.yearlyPlan : t.cart.monthlyPlan}
                        </div>

                        {/* Remove Button */}
                        <div className="flex items-center">
                          <Modal
                            title={`${t.cart.deleteTitle} ${getMultilingualText(item.product.title, language)}`}
                            message={t.cart.deleteMessage}
                            dangerOption={t.cart.delete}
                            cancelOption={t.cart.cancel}
                            dangerAction={(e) => handleRemove(e, item.id)}
                            cancelAction={() => setOpenModal(null)}
                            showModal={openModal === item.id}
                          />
                          <button
                            onClick={(e) => {
                              setOpenModal(item.id);
                            }}
                            type="button"
                            className="flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-500 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span className="hidden sm:inline">{t.cart.remove}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          )}

          {/* Order Summary */}
          {items.length > 0 && (
          <div className="bg-white rounded-xl shadow-subtle border border-blue-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t.cart.orderSummary}</h2>

            <div className="space-y-3 border-b border-blue-200 pb-4 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t.cart.subtotal} ({totalItems} {t.cart.items})</span>
                <span className="font-medium text-gray-900">€{totalAmount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t.cart.shipping}</span>
                <span className="text-gray-700">{t.cart.calculatedAtCheckout}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t.cart.taxes}</span>
                <span className="text-gray-700">{t.cart.calculatedAtCheckout}</span>
              </div>
            </div>

            <div className="flex justify-between text-base font-medium text-gray-900 mb-6">
              <span>{t.cart.total}</span>
              <span className="text-lg">€{totalAmount}</span>
            </div>

            {/* Checkout Button */}
            <button
              type="button"
              onClick={() => setIsPaymentModalOpen(true)}
              className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase shadow-sm hover:shadow"
            >
              <ShoppingBagIcon className="w-5 h-5" />
              {t.cart.proceedToCheckout}
            </button>

            {/* Continue Shopping */}
            <div className="mt-4 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                {t.cart.continueShopping}
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Payment Selection Modal */}
      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSelect={(method) => {
          setIsPaymentModalOpen(false);
          navigate(`/checkout?method=${method}`);
        }}
      />
    </>
  );
}
